import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import ModalShell from './ModalShell';
import { Button } from './FormElements';

const ConfirmContext = createContext(null);

// Usage: const confirm = useConfirm();
//        if (await confirm({ title, message, confirmText, tone: 'danger' })) { ... }
export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setDialog(typeof options === 'string' ? { message: options } : options);
  }), []);

  const settle = (value) => {
    if (resolverRef.current) resolverRef.current(value);
    resolverRef.current = null;
    setDialog(null);
  };

  const danger = dialog?.tone === 'danger';
  const Icon = danger ? ExclamationTriangleIcon : QuestionMarkCircleIcon;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ModalShell isOpen={Boolean(dialog)} onClose={() => settle(false)} panelClassName="sm:max-w-4xl" labelledBy="confirm-dialog-title">
        {dialog && (
          <>
            <div className="flex gap-3 px-3 sm:px-4 pt-3 sm:pt-4 pb-3">
              <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${danger ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h3 id="confirm-dialog-title" className="text-base font-semibold text-gray-900">{dialog.title || 'Are you sure?'}</h3>
                {dialog.message && <div className="mt-1 text-sm text-gray-600">{dialog.message}</div>}
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-3 sm:px-4 py-3 bg-gray-50 border-t border-gray-100 safe-area-bottom">
              <Button variant="outline" onClick={() => settle(false)}>{dialog.cancelText || 'Cancel'}</Button>
              <Button variant={danger ? 'danger' : 'primary'} onClick={() => settle(true)} data-autofocus>
                {dialog.confirmText || 'Confirm'}
              </Button>
            </div>
          </>
        )}
      </ModalShell>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  // Falls back to the native dialog if a component renders outside the provider (e.g. in tests)
  return confirm || ((options) => Promise.resolve(window.confirm(typeof options === 'string' ? options : options.message || options.title)));
};
